import React, { FC, useState, useCallback, useContext, useEffect } from 'react';
import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import { Label, Loader } from '@gravity-ui/uikit';
import clx from 'classnames';
import { useDropzone } from 'react-dropzone';
import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Settings';
import * as InstallableAppFormKeyset from 'i18n/InstallableAppForm';
import { AppVersionInfoWithTargetPlatforms, LicenseAgreement } from 'client/types';
import { ApiError } from 'client/api/error';
import { IconLoadFile } from '../icons/IconLoadFile';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import {
    installableAppUploadApk,
    installableAppUploadImage,
    installableAppUploadLicenseAgreement,
} from 'client/api/appStoreApi';
import { Channel } from 'client/model/skill';

import styles from './FilesUploaders.module.scss';

type DropzoneState = ReturnType<typeof useDropzone>;

const t = i18n(keyset);
const tInstallableAppForm = i18n(InstallableAppFormKeyset);

interface ScreenshotUploaderProps {
    channel: Channel;
    isDragActive: boolean;
    isLoading: boolean;
}

export const ScreenshotUploader = ({ channel, isDragActive, isLoading }: ScreenshotUploaderProps) => {
    const stylesContainer = clx(
        styles.appIcon__container,
        styles.appIcon_empty,
        channel === Channel.SmartScreenApp && styles.appIcon_smartScreen,
    );
    if (isLoading) {
        return (
            <div className={stylesContainer}>
                <Loader size="s" />
            </div>
        );
    }
    if (isDragActive) {
        return (
            <div className={stylesContainer}>
                <div>{tInstallableAppForm('load-here')}</div>
            </div>
        );
    }
    return (
        <div className={stylesContainer}>
            <IconLoadFile />
            <div className={styles.appIcon__resolution}>
                {channel === Channel.SmartScreenApp ? '1920x1200' : '1920x1080'}
            </div>
            <div className={styles.appIcon__label}>{tInstallableAppForm('screenshot')}</div>
        </div>
    );
};

const createLabel = (
    isLoading: boolean,
    error: string,
    isDragActive: boolean,
    disabled: boolean,
    dataInfo?: string,
) => {
    if (error) return <Label theme="danger">{error}</Label>;
    if (isLoading) return <Label theme="info">{`...${t('loading')}`}</Label>;
    if (dataInfo)
        return (
            <Label theme={disabled ? 'unknown' : 'info'}>
                {dataInfo.length > 30 ? `...${dataInfo.slice(dataInfo.length - 30)}` : dataInfo}
            </Label>
        );
    if (isDragActive) return <Label theme="normal">{tInstallableAppForm('drop-files-here')}</Label>;
    return <Label theme={disabled ? 'unknown' : 'normal'}>{tInstallableAppForm('drag-files-here')}</Label>;
};

export const FileUploaderView: FC<{
    disabled: boolean;
    error: string;
    isDragActive: boolean;
    isLoading: boolean;
    getInputProps: DropzoneState['getInputProps'];
    getRootProps: DropzoneState['getRootProps'];
    value?: string;
}> = ({ disabled, error, getInputProps, getRootProps, isDragActive, isLoading, value }) => (
    <div
        className={clx(
            'g-text-input',
            'g-text-input_view_normal',
            'g-text-input_size_xl',
            'g-text-input_pin_round-round',
            styles.uploaderContainer,
            disabled && 'g-text-input_disabled',
            disabled && styles.uploaderContainer_disabled,
        )}
    >
        <div className="g-text-input__content">
            <div
                className={clx(styles.uploaderContent, 'g-text-input__control', 'g-text-input__control_type_input')}
                {...getRootProps()}
            >
                <input {...getInputProps()} />
                {createLabel(isLoading, error, isDragActive, disabled, value)}
                <div className={styles.uploaderIcon}>
                    <FileArrowUp />
                </div>
            </div>
        </div>
    </div>
);

export const FileUploaderInput: FC<{
    disabled: boolean;
    uploadedData?: LicenseAgreement;
    onUploadComplete: (data?: unknown) => void;
}> = ({ disabled, onUploadComplete, uploadedData }) => {
    const { aliceApp } = useContext(CurrentAliceAppContext)!;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const onDrop = useCallback((acceptedFiles) => {
        setIsLoading(true);
        setError('');
        installableAppUploadLicenseAgreement(aliceApp.id, aliceApp.channel, acceptedFiles[0])
            .then((uploadedAgreement) => {
                onUploadComplete(uploadedAgreement);
                setIsLoading(false);
            })
            .catch((err) => {
                let { message } = err;

                if (err instanceof ApiError) {
                    if (err.code === 413) {
                        message = t('upload-error-max-size');
                    } else if (err.code === 415) {
                        message = t('upload-error-format');
                    }
                }
                onUploadComplete(undefined);
                setError(message);
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, disabled });

    return (
        <FileUploaderView
            disabled={disabled}
            error={error}
            getInputProps={getInputProps}
            getRootProps={getRootProps}
            isDragActive={isDragActive}
            isLoading={isLoading}
            value={uploadedData?.name}
        />
    );
};

export const ApkFileUploaderInput: FC<{
    disabled: boolean;
    updateAppVersion: (updatedValues: Partial<AppVersionInfoWithTargetPlatforms>) => void;
    validationError?: string;
    versionCode?: number;
    versionName?: string;
    versionNotes?: string;
}> = ({ disabled, validationError, versionCode, versionName, versionNotes, updateAppVersion }) => {
    const { aliceApp } = useContext(CurrentAliceAppContext)!;
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (versionName) {
            setUploadedFileName(versionName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDrop = useCallback(
        (acceptedFiles) => {
            setIsLoading(true);
            setError('');
            installableAppUploadApk({
                skillId: aliceApp.id,
                channel: aliceApp.channel,
                file: acceptedFiles[0],
                versionCode: versionCode!,
                versionName: versionName!,
                versionNotes: versionNotes,
            })
                .then((uploadResult) => {
                    setUploadedFileName(uploadResult.versionName);
                    updateAppVersion(uploadResult);
                    setIsLoading(false);
                })
                .catch((err) => {
                    let { message } = err;

                    if (err instanceof ApiError) {
                        if (err.code === 413) {
                            message = t('upload-error-max-size');
                        } else if (err.code === 400 || err.code === 415) {
                            message = t('upload-apk-error-format');
                        }
                    }
                    updateAppVersion({
                        versionCode,
                        versionName,
                        versionNotes,
                        appVersionId: undefined,
                        url: undefined,
                    });
                    setError(message);
                    setIsLoading(false);
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [versionName, versionCode, versionNotes, aliceApp.id],
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, disabled });

    return (
        <FileUploaderView
            disabled={disabled}
            error={error || (!isLoading && validationError) || ''}
            getInputProps={getInputProps}
            getRootProps={getRootProps}
            isDragActive={isDragActive}
            isLoading={isLoading}
            value={uploadedFileName}
        />
    );
};

const createAppIcon = (logoUrl: string, isLoading: boolean, isDragActive: boolean) => {
    if (isLoading) {
        return (
            <div className={clx(styles.appIcon__container, styles.appIcon_loading)}>
                <Loader size="s" />
            </div>
        );
    }
    if (isDragActive) {
        return (
            <div className={clx(styles.appIcon__container, styles.appIcon_empty)}>
                <div>{tInstallableAppForm('load-here')}</div>
            </div>
        );
    }
    if (logoUrl)
        return (
            <div
                className={clx(styles.appIcon__container, styles.appIcon_preview)}
                style={{ backgroundImage: `url(${logoUrl})` }}
            ></div>
        );
    return (
        <div className={clx(styles.appIcon__container, styles.appIcon_empty)}>
            <IconLoadFile />
            <div className={styles.appIcon__resolution}>{'1024x500'}</div>
            <div className={styles.appIcon__label}>{t('icon')}</div>
        </div>
    );
};

export const AppIconUploader: FC<{
    disabled: boolean;
    logoUrl: string;
    imageId: string | null;
    onUploadChange: (value: string | null, error?: string | undefined) => void;
}> = ({ disabled, imageId, logoUrl, onUploadChange }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [bannerImageUrl, setBannerImageUrl] = useState('');
    const { aliceApp } = useContext(CurrentAliceAppContext)!;

    useEffect(() => {
        setBannerImageUrl(logoUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDrop = useCallback((acceptedFiles) => {
        setIsLoading(true);
        installableAppUploadImage(aliceApp.id, aliceApp.channel, acceptedFiles[0], 'appTileImage')
            .then((image) => {
                setBannerImageUrl(image.url);
                onUploadChange(image.id, '');
                setIsLoading(false);
            })
            .catch((err) => {
                let { message } = err;

                if (err instanceof ApiError) {
                    if (err.code === 413) {
                        message = t('upload-error-max-size');
                    } else if (err.code === 415) {
                        message = t('upload-error-format');
                    }
                }
                onUploadChange(imageId, message);
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, disabled });

    return (
        <div
            className={clx(styles.uploaderContainer, disabled && styles.uploaderContainer_disabled)}
            {...getRootProps()}
        >
            <input {...getInputProps()} />
            {createAppIcon(bannerImageUrl, isLoading, isDragActive)}
        </div>
    );
};
